import { useMemo, useState } from "react";
import { api, useLoad } from "../api";
import { fmt } from "../lib/format";
import { linkHandler } from "../lib/router";
import { Hint } from "../components/Hint";
import { UsRulesSwitch, pf, usd, useUsRules } from "../components/UsRules";
import type { UsSlice, UsSummary } from "../types";

/**
 * Бэктест США (ТЗ США, задачи 3–4): итоги выбранного набора правил с 2001 г., половины истории (подбор 2001–2013,
 * проверка 2014–2026), по годам, рынкам и сторонам, сравнение наборов и список сделок. Один контракт на сделку,
 * доллары после издержек. Исследование с плацебо и портфелем — research/US_BACKTEST.md.
 */
export function UsBacktestPage() {
  const [rules, setRules] = useUsRules();
  const { data, error, loading } = useLoad((s) => api.usBacktest(rules, s), [rules]);
  const [market, setMarket] = useState("all");
  const trades = useMemo(
    () => (data?.trades ?? []).filter((t) => market === "all" || t.code === market).slice().reverse(),
    [data, market],
  );

  return (
    <main className="page journal-page backtest-page us">
      <a className="back" href="/us" onClick={linkHandler("/us")}>
        ← Рынки США
      </a>
      <h1>Бэктест США</h1>
      <p className="lede">
        Все сделки по правилам с 2001 года (Brent — с 2014), один контракт на сделку, в долларах после комиссии и
        проскальзывания. Половины истории — для честной проверки: правила и выбор лучшего варианта зафиксированы на
        2001–2013 гг., 2014–2026 гг. — проверка без подстройки.
      </p>
      <p className="explain">
        <b>Прототип: цены Yahoo без склейки.</b> На смене контракта цена прыгает на разницу между контрактами — это
        искажает результат сделок, переживших смену (оценка — в research/US_BACKTEST.md).
      </p>
      <UsRulesSwitch rules={rules} onChange={setRules} />
      {error ? <p className="form-error" role="alert">Не удалось загрузить: {error}</p> : null}
      {loading && !data ? <div className="skeleton" aria-busy="true">Считаем бэктест…</div> : null}

      {data ? (
        <>
          <section>
            <h2>{data.title}</h2>
            <dl className="facts">
              <Card label="Вся история" s={data.all} />
              <Card label="2001–2013 (подбор)" s={data.early} />
              <Card label="2014–2026 (проверка)" s={data.late} />
            </dl>
            <p className="explain">{data.research}</p>
          </section>

          <section>
            <h2>
              Сравнение наборов <Hint>Одни и те же данные и исполнение, разные правила сетапа и стопа. PF — прибыль на доллар убытка после издержек: выше 1 — система зарабатывает.</Hint>
            </h2>
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th className="text">Набор</th>
                    <th>Сделок</th>
                    <th>% приб.</th>
                    <th>PF</th>
                    <th>Итог, $</th>
                    <th>PF 2001–2013</th>
                    <th>PF 2014–2026</th>
                  </tr>
                </thead>
                <tbody>
                  {data.variants.map((v) => (
                    <tr key={v.key} className={v.key === data.rules ? "active-row" : undefined}>
                      <td className="text">
                        <b>{v.key}</b> {v.title}
                      </td>
                      <td>{fmt.int(v.all.n)}</td>
                      <td>{fmt.pct(v.all.winPct)}</td>
                      <td>{pf(v.all.pfNet)}</td>
                      <td>{usd(v.all.netUsd)}</td>
                      <td>{pf(v.early.pfNet)} <span className="muted">({fmt.int(v.early.n)})</span></td>
                      <td>{pf(v.late.pfNet)} <span className="muted">({fmt.int(v.late.n)})</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <SliceTable title="По годам" hint="Год — по дате входа. Видно, насколько результат неровный: несколько лет дают основную прибыль." rows={data.years} />
          <SliceTable title="По рынкам" hint="Рынки сильно связаны (нефть WTI и Brent, золото и серебро, S&P и Nasdaq): независимых ставок меньше, чем рынков." rows={data.markets} />
          <SliceTable title="Покупки и продажи" hint="Перевходы — повторные входы по максимуму 13 дней после выбивания стопом (только у наборов по книге)." rows={data.sides} />

          <section>
            <h2>Как считается</h2>
            <ul className="plain-list">
              {data.method.map((m, i) => (
                <li key={i}>{m}</li>
              ))}
              <li>
                Стопов, сработавших гэпом в окне смены контракта Yahoo (возможный артефакт ряда): {fmt.int(data.rollGapStops)}.
              </li>
            </ul>
            <p className="muted">{data.note}</p>
          </section>

          <section>
            <h2>Сделки</h2>
            <div className="list-tools">
              <label>
                Рынок{" "}
                <select value={market} onChange={(e) => setMarket(e.target.value)}>
                  <option value="all">все</option>
                  {data.markets.map((m) => (
                    <option key={m.key} value={m.key}>
                      {m.key}
                    </option>
                  ))}
                </select>
              </label>
              <span className="muted">{fmt.int(trades.length)} сделок, новые сверху</span>
            </div>
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th className="text">Рынок</th>
                    <th className="text">Сторона</th>
                    <th className="text">Отчёт → публикация</th>
                    <th className="text">Вход</th>
                    <th className="text">Выход</th>
                    <th className="text">Причина</th>
                    <th>Итог, $</th>
                  </tr>
                </thead>
                <tbody>
                  {trades.slice(0, 300).map((t, i) => (
                    <tr key={`${t.code}-${t.entry}-${i}`}>
                      <td className="text">
                        <a href={`/us/${t.code}`} onClick={linkHandler(`/us/${t.code}`)}>{t.code}</a>
                      </td>
                      <td className={`text ${t.dir > 0 ? "up" : "down"}`}>{t.dir > 0 ? "покупка" : "продажа"}</td>
                      <td className="text">{t.reentry ? "перевход" : `${fmt.date(t.report)} → ${fmt.date(t.published)}`}</td>
                      <td className="text">{fmt.date(t.entry)} · {fmt.price(t.entryPrice)}</td>
                      <td className="text">{fmt.date(t.exit)}</td>
                      <td className="text">
                        {t.reason}
                        {t.rollGapStop ? <span className="muted" title="Стоп сработал гэпом в окне смены контракта Yahoo"> · смена контракта?</span> : null}
                      </td>
                      <td className={t.netUsd > 0 ? "up" : t.netUsd < 0 ? "down" : undefined}>{usd(t.netUsd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {trades.length > 300 ? <p className="muted">Показаны последние 300.</p> : null}
          </section>
        </>
      ) : null}
    </main>
  );
}

function Card({ label, s }: { label: string; s: UsSummary }) {
  return (
    <div className="fact">
      <dt>{label}</dt>
      <dd className={s.pfNet > 1 ? "zone-bull" : s.pfNet > 0 ? "zone-bear" : undefined}>PF {pf(s.pfNet)}</dd>
      <dd className="note">
        {fmt.int(s.n)} сделок, {fmt.pct(s.winPct)} прибыльных, итог {usd(s.netUsd)}; средняя прибыль {usd(s.avgWin)}, средний
        убыток {usd(s.avgLoss)}; PF до издержек {pf(s.pfGross)}
      </dd>
    </div>
  );
}

function SliceTable({ title, hint, rows }: { title: string; hint: string; rows: UsSlice[] }) {
  return (
    <section>
      <h2>
        {title} <Hint>{hint}</Hint>
      </h2>
      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              <th className="text"> </th>
              <th>Сделок</th>
              <th>% приб.</th>
              <th>
                PF <Hint>Прибыль на доллар убытка после издержек.</Hint>
              </th>
              <th>Итог, $</th>
              <th>Ср. прибыль, $</th>
              <th>Ср. убыток, $</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key}>
                <td className="text">{r.key}</td>
                <td>{fmt.int(r.s.n)}</td>
                <td>{r.s.n ? fmt.pct(r.s.winPct) : "—"}</td>
                <td className={r.s.pfNet > 1 ? "up" : r.s.n ? "down" : undefined}>{pf(r.s.pfNet)}</td>
                <td className={r.s.netUsd > 0 ? "up" : r.s.netUsd < 0 ? "down" : undefined}>{usd(r.s.netUsd)}</td>
                <td>{usd(r.s.avgWin)}</td>
                <td>{usd(r.s.avgLoss)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
